import React, { useEffect, useState } from "react";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
    AlertTriangle, CheckCircle2, XCircle, ExternalLink, FileEdit,
    FileText, Download,
} from "lucide-react";
import { resolveTollStatus } from "@/lib/statusResolvers";

function fmtMoney(cents) {
    if (cents == null) return "—";
    const sign = cents < 0 ? "-" : "";
    const v = Math.abs(cents) / 100;
    return `${sign}$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtISO(s) { return s ? String(s).replace("T", " ").slice(0, 19) : "—"; }

/**
 * Phase TOLLBILL-P3a + P4b (2026-07-10) — Toll transaction detail drawer.
 *
 * Phase 4b restructure:
 *   * Customer/Rental fields render human labels (customer_name /
 *     rental_unit_number) from the enriched detail response.
 *   * Location field renders authority as a muted subtitle.
 *   * Raw payload JSON moved to a compact lower-left collapsible.
 *   * New Attachment section (right column, lower half) — shows
 *     filename + Download PDF button when has_attachment is true, or a
 *     muted placeholder otherwise.
 *
 * Actions (permission-gated by ``canManage``): reassign, mark-disputed,
 * resolve-dispute. Terminal states (invoiced/void) render a read-only
 * banner and hide action buttons.
 */
export default function TollTransactionDetailModal({ open, onOpenChange, txnId, canManage, onChanged }) {
    const [txn, setTxn] = useState(null);
    const [loading, setLoading] = useState(false);
    const [reassignOpen, setReassignOpen] = useState(false);
    const [disputeOpen, setDisputeOpen] = useState(false);
    const [resolveOpen, setResolveOpen] = useState(false);

    const [customerId, setCustomerId] = useState("");
    const [rentalId, setRentalId] = useState("");
    const [disputeReason, setDisputeReason] = useState("");
    const [resolveOutcome, setResolveOutcome] = useState("billable");
    const [resolveNote, setResolveNote] = useState("");
    const [saving, setSaving] = useState(false);
    const [attachDownloading, setAttachDownloading] = useState(false);

    useEffect(() => {
        if (!open || !txnId) return;
        setLoading(true);
        setReassignOpen(false); setDisputeOpen(false); setResolveOpen(false);
        setDisputeReason(""); setResolveNote(""); setResolveOutcome("billable");
        api.get(`/admin/tolls/transactions/${encodeURIComponent(txnId)}`)
            .then(({ data }) => {
                setTxn(data);
                setCustomerId(data.customer_id || "");
                setRentalId(data.rental_id || "");
            })
            .catch((err) => { toast.error(formatApiError(err)); onOpenChange(false); })
            .finally(() => setLoading(false));
    }, [open, txnId, onOpenChange]);

    const reload = async () => {
        try {
            const { data } = await api.get(`/admin/tolls/transactions/${encodeURIComponent(txnId)}`);
            setTxn(data);
            if (onChanged) onChanged();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const submitReassign = async () => {
        setSaving(true);
        try {
            await api.patch(`/admin/tolls/transactions/${encodeURIComponent(txn.transaction_id)}`, {
                customer_id: customerId.trim() || null,
                rental_id: rentalId.trim() || null,
            });
            toast.success("Reassigned");
            setReassignOpen(false);
            await reload();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    const submitDispute = async () => {
        if (!disputeReason.trim()) { toast.error("Reason is required"); return; }
        setSaving(true);
        try {
            await api.post(`/admin/tolls/transactions/${encodeURIComponent(txn.transaction_id)}/mark-disputed`, {
                reason: disputeReason.trim(),
            });
            toast.success("Marked as disputed");
            setDisputeOpen(false);
            await reload();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    const submitResolve = async () => {
        setSaving(true);
        try {
            await api.post(`/admin/tolls/transactions/${encodeURIComponent(txn.transaction_id)}/resolve-dispute`, {
                outcome: resolveOutcome,
                note: resolveNote.trim() || null,
            });
            toast.success(`Dispute resolved: ${resolveOutcome}`);
            setResolveOpen(false);
            await reload();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    // Phase 4b — download the PDF attachment via the JWT-authenticated
    // /attachment endpoint. Uses responseType=blob so the browser gets
    // real binary bytes, then invokes a hidden <a href=blob download>.
    const downloadAttachment = async () => {
        if (!txn?.attachment_file_id) return;
        setAttachDownloading(true);
        try {
            const resp = await api.get(
                `/admin/tolls/transactions/${encodeURIComponent(txn.transaction_id)}/attachment`,
                { responseType: "blob" },
            );
            const blob = new Blob([resp.data], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = txn.attachment_filename || `toll-bill-${txn.transaction_id}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            // Delay revoke slightly so the browser has time to start the download.
            setTimeout(() => URL.revokeObjectURL(url), 4000);
            toast.success("Downloaded");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setAttachDownloading(false); }
    };

    const openAttachmentInNewTab = async () => {
        if (!txn?.attachment_file_id) return;
        setAttachDownloading(true);
        try {
            const resp = await api.get(
                `/admin/tolls/transactions/${encodeURIComponent(txn.transaction_id)}/attachment`,
                { responseType: "blob" },
            );
            const blob = new Blob([resp.data], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            window.open(url, "_blank", "noopener");
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setAttachDownloading(false); }
    };

    const status = txn ? resolveTollStatus(txn) : null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="toll-detail-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        Toll transaction
                        {status && (
                            <span
                                data-testid="toll-detail-status-chip"
                                className={`inline-flex items-center rounded-full ring-1 px-2 py-0.5 text-[11px] font-semibold ${status.tone}`}
                            >
                                {status.label}
                            </span>
                        )}
                        {txn?.dispute_flag && !status?.isDisputed && (
                            <span className="inline-flex items-center gap-1 text-red-700 text-[11px] font-medium">
                                <AlertTriangle size={12} /> Dispute flag
                            </span>
                        )}
                    </DialogTitle>
                    <DialogDescription>
                        {txn ? <span className="font-mono">{txn.transaction_id}</span> : "Loading…"}
                    </DialogDescription>
                </DialogHeader>

                {loading && <p className="text-sm text-slate-500 py-6" data-testid="toll-detail-loading">Loading…</p>}

                {!loading && txn && (
                    <div className="space-y-4">
                        {/* Two-column body */}
                        <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                            {/* Left column */}
                            <div>
                                <Label className="text-xs text-slate-500">Posted at</Label>
                                <p className="text-slate-900" data-testid="toll-detail-posted-at">{fmtISO(txn.posted_at)}</p>
                            </div>
                            {/* Right column */}
                            <div>
                                <Label className="text-xs text-slate-500">Amount</Label>
                                <p className="font-semibold text-slate-900 tabular-nums" data-testid="toll-detail-amount">
                                    {fmtMoney(txn.amount_cents)} {txn.currency || ""}
                                </p>
                            </div>
                            <div>
                                <Label className="text-xs text-slate-500">Plate</Label>
                                <p className="text-slate-900 font-mono" data-testid="toll-detail-plate">{txn.plate || "—"}</p>
                            </div>
                            <div>
                                <Label className="text-xs text-slate-500">Location</Label>
                                <p className="text-slate-900" data-testid="toll-detail-location">{txn.location || "—"}</p>
                                {txn.authority && (
                                    <p className="text-xs text-slate-500 mt-0.5" data-testid="toll-detail-authority">{txn.authority}</p>
                                )}
                            </div>
                            <div>
                                <Label className="text-xs text-slate-500">Customer</Label>
                                <p className="text-slate-900" data-testid="toll-detail-customer">
                                    {txn.customer_id ? (
                                        <Link to={`/admin/customers/${txn.customer_id}`} className="text-blue-900 hover:underline inline-flex items-center gap-1">
                                            {txn.customer_name || `${txn.customer_id.slice(0, 8)}…`} <ExternalLink size={11} />
                                        </Link>
                                    ) : <span className="text-slate-400">Unresolved</span>}
                                </p>
                            </div>
                            <div>
                                <Label className="text-xs text-slate-500">Rental</Label>
                                <p className="text-slate-900" data-testid="toll-detail-rental">
                                    {txn.rental_id ? (
                                        <Link to={`/admin/rentals/${txn.rental_id}`} className="text-blue-900 hover:underline inline-flex items-center gap-1">
                                            {txn.rental_unit_number || `${txn.rental_id.slice(0, 8)}…`} <ExternalLink size={11} />
                                        </Link>
                                    ) : <span className="text-slate-400">Unresolved</span>}
                                </p>
                            </div>
                            <div>
                                <Label className="text-xs text-slate-500">External source</Label>
                                <p className="text-slate-900">{txn.external_source || "—"}</p>
                            </div>
                            <div>
                                <Label className="text-xs text-slate-500">Invoice</Label>
                                <p className="text-slate-900" data-testid="toll-detail-invoice">
                                    {txn.invoice_id ? (
                                        <Link to={`/admin/invoices/${txn.invoice_id}`} className="text-blue-900 hover:underline inline-flex items-center gap-1">
                                            View invoice <ExternalLink size={11} />
                                        </Link>
                                    ) : <span className="text-slate-400">Not yet invoiced</span>}
                                </p>
                            </div>
                            {txn.dispute_reason && (
                                <div className="col-span-2">
                                    <Label className="text-xs text-slate-500">Dispute reason</Label>
                                    <p className="text-slate-900 text-sm rounded-md bg-red-50 border border-red-100 px-3 py-2">{txn.dispute_reason}</p>
                                </div>
                            )}
                        </div>

                        {/* Phase 4b — Attachment + Raw payload two-column footer */}
                        <div className="grid grid-cols-2 gap-4">
                            {/* Left: Raw payload (compact) */}
                            <details className="rounded-md border border-slate-200 bg-slate-50/50 text-xs">
                                <summary className="cursor-pointer font-medium text-slate-600 px-3 py-1.5" data-testid="toll-detail-raw-toggle">
                                    Raw payload (JSON)
                                </summary>
                                <pre className="text-[10.5px] font-mono text-slate-700 px-3 py-2 whitespace-pre-wrap max-h-56 overflow-auto" data-testid="toll-detail-raw">
{JSON.stringify(txn.raw_payload || {}, null, 2)}
                                </pre>
                            </details>

                            {/* Right: Attachment section */}
                            <div
                                className="rounded-md border border-slate-200 bg-white p-3 text-xs"
                                data-testid="toll-detail-attachment-section"
                            >
                                <p className="font-medium text-slate-600 flex items-center gap-1.5 mb-2">
                                    <FileText size={13} /> Attachment
                                </p>
                                {txn.has_attachment || txn.attachment_file_id ? (
                                    <div className="space-y-2" data-testid="toll-detail-attachment-present">
                                        <p className="text-slate-800 truncate" title={txn.attachment_filename || ""} data-testid="toll-detail-attachment-filename">
                                            {txn.attachment_filename || "toll-bill.pdf"}
                                        </p>
                                        <div className="flex gap-1.5 flex-wrap">
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={openAttachmentInNewTab}
                                                disabled={attachDownloading}
                                                className="gap-1 h-7 px-2 text-[11px]"
                                                data-testid="toll-detail-attachment-view"
                                            >
                                                <ExternalLink size={11} /> View
                                            </Button>
                                            <Button
                                                size="sm"
                                                onClick={downloadAttachment}
                                                disabled={attachDownloading}
                                                className="bg-blue-900 hover:bg-blue-800 gap-1 h-7 px-2 text-[11px]"
                                                data-testid="toll-detail-attachment-download"
                                            >
                                                <Download size={11} /> {attachDownloading ? "…" : "Download PDF"}
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    <p className="text-slate-400 italic" data-testid="toll-detail-attachment-empty">
                                        No bill PDF attached
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Action panels */}
                        {canManage && !status?.isTerminal && (
                            <div className="space-y-2 border-t border-slate-100 pt-4">
                                <div className="flex flex-wrap gap-2">
                                    <Button variant="outline" size="sm" onClick={() => setReassignOpen((v) => !v)} data-testid="toll-detail-reassign-toggle" className="gap-1.5">
                                        <FileEdit size={14} /> Reassign
                                    </Button>
                                    {!status?.isDisputed && (
                                        <Button variant="outline" size="sm" onClick={() => { setDisputeOpen((v) => !v); setResolveOpen(false); }} data-testid="toll-detail-dispute-toggle" className="gap-1.5 text-red-700 border-red-200 hover:bg-red-50">
                                            <AlertTriangle size={14} /> Mark disputed
                                        </Button>
                                    )}
                                    {status?.isDisputed && (
                                        <Button variant="outline" size="sm" onClick={() => { setResolveOpen((v) => !v); setDisputeOpen(false); }} data-testid="toll-detail-resolve-toggle" className="gap-1.5">
                                            <CheckCircle2 size={14} /> Resolve dispute
                                        </Button>
                                    )}
                                </div>

                                {reassignOpen && (
                                    <div className="rounded-md border border-slate-200 bg-white p-3 space-y-2" data-testid="toll-detail-reassign-panel">
                                        <div>
                                            <Label className="text-xs">Customer ID</Label>
                                            <Input value={customerId} onChange={(e) => setCustomerId(e.target.value)} placeholder="uuid or blank to clear" data-testid="toll-detail-reassign-customer" />
                                        </div>
                                        <div>
                                            <Label className="text-xs">Rental ID</Label>
                                            <Input value={rentalId} onChange={(e) => setRentalId(e.target.value)} placeholder="uuid or blank to clear" data-testid="toll-detail-reassign-rental" />
                                        </div>
                                        <div className="flex justify-end gap-2 pt-1">
                                            <Button variant="ghost" size="sm" onClick={() => setReassignOpen(false)}>Cancel</Button>
                                            <Button size="sm" onClick={submitReassign} disabled={saving} data-testid="toll-detail-reassign-save">Save</Button>
                                        </div>
                                    </div>
                                )}
                                {disputeOpen && (
                                    <div className="rounded-md border border-red-200 bg-red-50/40 p-3 space-y-2" data-testid="toll-detail-dispute-panel">
                                        <Label className="text-xs">Reason (required)</Label>
                                        <Textarea value={disputeReason} onChange={(e) => setDisputeReason(e.target.value)} placeholder="e.g. Wrong plate captured" data-testid="toll-detail-dispute-reason" rows={2} />
                                        <div className="flex justify-end gap-2">
                                            <Button variant="ghost" size="sm" onClick={() => setDisputeOpen(false)}>Cancel</Button>
                                            <Button size="sm" className="bg-red-700 hover:bg-red-800" onClick={submitDispute} disabled={saving} data-testid="toll-detail-dispute-submit">Mark disputed</Button>
                                        </div>
                                    </div>
                                )}
                                {resolveOpen && (
                                    <div className="rounded-md border border-slate-200 bg-white p-3 space-y-3" data-testid="toll-detail-resolve-panel">
                                        <div>
                                            <Label className="text-xs">Outcome</Label>
                                            <div className="flex gap-2 mt-1">
                                                <Button variant={resolveOutcome === "billable" ? "default" : "outline"} size="sm" onClick={() => setResolveOutcome("billable")} data-testid="toll-detail-resolve-billable" className="gap-1.5">
                                                    <CheckCircle2 size={14} /> Billable
                                                </Button>
                                                <Button variant={resolveOutcome === "voided" ? "default" : "outline"} size="sm" onClick={() => setResolveOutcome("voided")} data-testid="toll-detail-resolve-voided" className="gap-1.5">
                                                    <XCircle size={14} /> Voided
                                                </Button>
                                            </div>
                                            <p className="text-[11px] text-slate-500 mt-1">
                                                {resolveOutcome === "billable"
                                                    ? "Restores the transaction so it can be included in the next invoice run."
                                                    : "Removes the transaction from future invoicing entirely."}
                                            </p>
                                        </div>
                                        <div>
                                            <Label className="text-xs">Note (optional)</Label>
                                            <Textarea value={resolveNote} onChange={(e) => setResolveNote(e.target.value)} placeholder="Context for the audit log" data-testid="toll-detail-resolve-note" rows={2} />
                                        </div>
                                        <div className="flex justify-end gap-2">
                                            <Button variant="ghost" size="sm" onClick={() => setResolveOpen(false)}>Cancel</Button>
                                            <Button size="sm" onClick={submitResolve} disabled={saving} data-testid="toll-detail-resolve-submit">Resolve</Button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {status?.isTerminal && (
                            <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-[11px] text-slate-600">
                                Terminal state — this transaction can no longer be edited.
                            </div>
                        )}
                    </div>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="toll-detail-close">Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
